import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { CustomPaging, CustomTreeData, PagingState, SortingState, TreeDataState } from '@devexpress/dx-react-grid';
import { Grid, PagingPanel, TableHeaderRow, TableTreeColumn, VirtualTable } from '@devexpress/dx-react-grid-material-ui';
import { Paper } from '@mui/material';
import TableLoading from '@/components/SearchTable/TableLoading/TableLoading';
import { ASC_ORDER } from '@/helpers/constants';
import { processSorting } from '@/helpers/tables-utils';
import useApiRequest from '@/hooks/useApiRequest';
import useAppLanguage from '@/hooks/useAppLanguage';
import useToastContext from '@/hooks/useToastContext';
import campaignServices from '@/services/campaignServices';
import { SortingDirection, StatusSummaryTableDto } from '@/types';
import { StatusConfig } from './StatusSummary';
import styles from './Dashboard.module.scss';

type StatusSummaryCRsTableProps = {
  crStatusSelected: string;
  subCRStatusSelected?: string;
  statusConfig: StatusConfig;
  currentGroup: string;
};

type PaginationConfig = {
  pageSize: number;
  currentPage: number;
  availablePages: number;
};

type Sorting = {
  columnName: string;
  direction: SortingDirection;
};

const columnExtensions: VirtualTable.ColumnExtension[] = [
  { columnName: 'id', width: 320 },
  { columnName: 'plant', width: 85 },
  { columnName: 'processPhase', width: 230 },
  { columnName: 'task', width: 140 },
  { columnName: 'assignedAM', width: 270 },
  { columnName: 'role', width: 180 },
  { columnName: 'vehicleCount', width: 130, align: 'center' },
];

const columnExtensionsOrdering: SortingState.ColumnExtension[] = [
  { columnName: 'id', sortingEnabled: true },
  { columnName: 'plant', sortingEnabled: false },
  { columnName: 'processPhase', sortingEnabled: true },
  { columnName: 'task', sortingEnabled: false },
  { columnName: 'assignedAM', sortingEnabled: true },
  { columnName: 'role', sortingEnabled: false },
  { columnName: 'vehicleCount', sortingEnabled: false },
];

const defaultSorting: Sorting[] = [
  { columnName: 'id', direction: ASC_ORDER },
];

const orderToDto = {
  processPhase: 'status',
  assignedAM: 'assignedUser.summary',
};

const defaultPaginationConfig = {
  pageSize: 50,
  currentPage: 0,
  availablePages: 1,
};

const groupSelectedTogroupDto = {
  PLANT: 'plant',
  MODEL_SERIES: 'modelSeries',
  DEPARTMENT: 'department',
};

const formatStatus = (status: string) => status.replaceAll('_', ' ').toLowerCase().replace(/(?:^|\s)\S/g, (a) => a.toUpperCase());

const StatusSummaryCRsTable: React.FC<StatusSummaryCRsTableProps> = (props) => {
  const { crStatusSelected, subCRStatusSelected, statusConfig, currentGroup } = props;
  const { t } = useTranslation();
  const lang = useAppLanguage();
  const { addToast } = useToastContext();
  const fetchStatusSummaryTable = useApiRequest(campaignServices.getStatusSummaryTableDashboard);
  const [rowsRaw, setRowsRaw] = useState<any[]>([]);
  const [rows, setRows] = useState<any[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [sorting, setSorting] = useState<Sorting[]>(defaultSorting);
  const [paginationConfig, setPaginationConfig] = useState<PaginationConfig>(defaultPaginationConfig);
  const [expandedRows, setExpandedRows] = useState<string[]>([]);

  const columns = [
    { name: 'id', title: t('ID') },
    { name: 'plant', title: t('plant') },
    { name: 'task', title: t('campaignRequests.assignment') },
    { name: 'assignedAM', title: t('campaignRequests.assignedUser') },
    { name: 'role', title: t('campaignRequests.role') },
    { name: 'processPhase', title: t('campaignRequests.processPhase') },
    { name: 'vehicleCount', title: t('campaignRequests.vehicleCount') },
  ];

  const adaptRows = (rows: any[]) => rows.map((row) => {
    const newRow = {
      ...row,
      task: row.task === 'ASSIGNED' ? t('campaignRequests.assigned') : t('campaignRequests.notAssigned'),
      role: row.role ? formatStatus(row.role) : '',
      processPhase: row.processPhase ? t([`campaign.${row.processPhase}`, formatStatus(row.processPhase)]) : '',
    };

    if (row.subCampaigns.length === 0) {
      delete newRow.subCampaigns;
    } else {
      newRow.subCampaigns = row.subCampaigns.map((subRow) => ({
        ...subRow,
        task: subRow.task === 'ASSIGNED' ? t('campaignRequests.assigned') : t('campaignRequests.notAssigned'),
        role: subRow.role ? formatStatus(subRow.role) : '',
        processPhase: subRow.processPhase ? t([`subCampaign.${subRow.processPhase}`, formatStatus(subRow.processPhase)]) : '',
      }));
    }

    return newRow;
  });

  const getTable = (currentPage: number, currentSort: Sorting[], pageSize: number) => {
    setLoading(true);
    const sortingg = `${processSorting(`campaigns.${orderToDto?.[currentSort[0].columnName] || currentSort[0].columnName}`)},${currentSort[0].direction}`;
    const groupBy = statusConfig.groupingLevels === 'ALL' ? null : groupSelectedTogroupDto[statusConfig.groupingLevels];

    fetchStatusSummaryTable(crStatusSelected, subCRStatusSelected, groupBy, currentGroup, currentPage, pageSize, sortingg)
      .then((res: StatusSummaryTableDto) => {
        setRowsRaw(res.content);
        setRows(adaptRows(res.content));
        setPaginationConfig((prev) => ({ ...prev, availablePages: res.totalElements, currentPage }));

        const expandedRows = subCRStatusSelected ? res.content.map((row) => row.id) : [];
        setExpandedRows(expandedRows);
      })
      .catch((error) => addToast(error.message, 'ERROR'))
      .finally(() => setLoading(false));
  };

  const handlePageChange = (page: number) => {
    setPaginationConfig((prev) => ({ ...prev, currentPage: page }));
    getTable(page, sorting, paginationConfig.pageSize);
  };

  const handlePageSizeChange = (pageSize: number) => {
    setPaginationConfig((prev) => ({ ...prev, pageSize, currentPage: 0 }));
    getTable(0, sorting, pageSize);
  };

  const handleSortingChange = (soorting: Sorting[]) => {
    setSorting(soorting);
    getTable(paginationConfig.currentPage, soorting, paginationConfig.pageSize);
  };

  const handleExpandedRowsChange = (expandedRows: (string | number)[]) => {
    setExpandedRows(expandedRows as string[]);
  };

  useEffect(() => {
    setRows(adaptRows(rowsRaw));
  }, [lang]);

  useEffect(() => {
    getTable(0, sorting, paginationConfig.pageSize);
  }, [crStatusSelected, subCRStatusSelected, statusConfig.filterDays, statusConfig.groupingLevels, currentGroup]);

  const columnsToShow = expandedRows.length === 0 ? columns.filter((column) => column.name !== 'plant') : columns;

  return (
    <div>
      <Paper style={{ position: 'relative' }}>
        <Grid
          rows={rows}
          columns={columnsToShow}
          getRowId={(row) => row.id}
        >
          <SortingState
            sorting={sorting}
            onSortingChange={handleSortingChange}
            columnExtensions={columnExtensionsOrdering}
          />
          <PagingState
            currentPage={paginationConfig.currentPage}
            onCurrentPageChange={handlePageChange}
            pageSize={paginationConfig.pageSize}
            onPageSizeChange={handlePageSizeChange}
          />
          <TreeDataState expandedRowIds={expandedRows} onExpandedRowIdsChange={handleExpandedRowsChange} />
          <CustomTreeData getChildRows={(row, rootRows) => {
            return (row ? row?.subCampaigns : rootRows);
          }}
          />
          <VirtualTable columnExtensions={columnExtensions} />
          <TableHeaderRow showSortingControls />
          <TableTreeColumn for="id" />
          <CustomPaging totalCount={paginationConfig.availablePages} />
          <PagingPanel pageSizes={[10, 20, 50, 100]} />

          {loading && <TableLoading styles={styles} />}
        </Grid>
      </Paper>
      <br />
      <br />
      <br />
    </div>
  );
};

export default StatusSummaryCRsTable;
